import type { ConnectionCheckReport, RegistryApp } from '@/api/console';

/**
 * EAA-1709: the fields a connection check actually exercises. A check run
 * against one set of these says nothing about another, so the approval gate is
 * keyed on this fingerprint — editing any of them re-hides "Duyệt" until the
 * operator checks again.
 */
export function appConnectionFingerprint(app: RegistryApp): string {
    return JSON.stringify([
        app.app_type,
        app.web_url,
        app.api_base,
        app.audience,
        app.redirect_uris,
        app.post_logout_redirect_uris,
        app.kc_client_id,
        app.kc_api_client_id,
        app.kc_secret_rotated_at,
        app.kc_api_secret_rotated_at,
    ]);
}

/** Same rule the server's `/approve` enforces: any `failed` probe blocks; `degraded` does not. */
export function hasFailedProbe(report: ConnectionCheckReport): boolean {
    return report.overall === 'failed' || report.probes.some((probe) => probe.status === 'failed');
}

export interface ApprovalCheck {
    code: string;
    fingerprint: string;
    report: ConnectionCheckReport;
}

/**
 * True only when THIS session's latest check was for this app, against its
 * current connection config, and returned no failed probe. An enabled app has
 * nothing to approve.
 */
export function isApprovalUnlocked(
    check: ApprovalCheck | null,
    app: RegistryApp | null | undefined
): boolean {
    if (!check || !app || app.enabled) return false;
    if (check.code !== app.code) return false;
    if (check.fingerprint !== appConnectionFingerprint(app)) return false;
    return !hasFailedProbe(check.report);
}
